import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import gsap from "gsap";
import { StaticImageData } from "next/image";
import { useTranslation } from "next-i18next";

import Advantage from "../../components/Advantage";
import OutlinedBtn from "../../components/Buttons/OutlinedButton";
import CustomHead from "../../components/CustomHead";
import Discuss from "../../components/Discuss";
import Map from "../../components/Map";
import MaxWidth from "../../components/MaxWidth";
import PageDescription from "../../components/PageDescription";
import PageWrapper from "../../components/PageWrapper";
import SloganLink from "../../components/SloganLink";
import { useAppProvider } from "../../utils/appProvider";

import styles from "./Partners.module.scss";

import CaseIcon from "../../public/imgs/icons/case_icon.svg?url";
import ScreenIcon from "../../public/imgs/icons/screen_icon.svg?url";
import LogoItv from "../../public/imgs/partners/logo-itv.png";
import LogoItvXS from "../../public/imgs/partners/logo-itv-xs.png";
import LogoIss from "../../public/imgs/partners/logo-iss.png";
import LogoIssXS from "../../public/imgs/partners/logo-iss-xs.png";
import LogoRecfaces from "../../public/imgs/partners/logo-recfaces.png";
import LogoRecfacesXS from "../../public/imgs/partners/logo-recfaces-xs.png";
import LogoSigur from "../../public/imgs/partners/logo-sigur.png";
import LogoSigurXS from "../../public/imgs/partners/logo-sigur-xs.png";
import LogoNbki from "../../public/imgs/partners/logo-nbki.png";
import LogoNbkiXS from "../../public/imgs/partners/logo-nbki-xs.png";
import LogoBolid from "../../public/imgs/partners/logo-bolid.png";
import LogoBolidXS from "../../public/imgs/partners/logo-bolid-xs.png";
import LogoPromobot from "../../public/imgs/partners/logo-promobot.png";
import LogoPromobotXS from "../../public/imgs/partners/logo-promobot-xs.png";
import LogoBit from "../../public/imgs/partners/logo-bit.png";
import LogoBitXS from "../../public/imgs/partners/logo-bit-xs.png";
import GridContainer from "../../components/GridContainer/GridContainer";
import PartnerCard from "../../components/Cards/PartnerCard/PartnerCard";

export type Partner = {
  logo: StaticImageData;
  logoSmall?: StaticImageData;
  title: string;
  desc: string;
};

const partners: Partner[] = [
  {
    logo: LogoItv,
    logoSmall: LogoItvXS,
    title: "ITV",
    desc: "ITV",
  },
  {
    logo: LogoIss,
    logoSmall: LogoIssXS,
    title: "ISS",
    desc: "ISS",
  },
  {
    logo: LogoRecfaces,
    logoSmall: LogoRecfacesXS,
    title: "RecFaces",
    desc: "RecFaces",
  },

  {
    logo: LogoSigur,
    logoSmall: LogoSigurXS,
    title: "SIGUR",
    desc: "SIGUR",
  },
  {
    logo: LogoNbki,
    logoSmall: LogoNbkiXS,
    title: "НБКИ",
    desc: "НБКИ",
  },
  {
    logo: LogoBolid,
    logoSmall: LogoBolidXS,
    title: "Bolid",
    desc: "Bolid",
  },
  {
    logo: LogoPromobot,
    logoSmall: LogoPromobotXS,
    title: "PROMOBOT",
    desc: "PROMOBOT",
  },
  {
    logo: LogoBit,
    logoSmall: LogoBitXS,
    title: "BIT",
    desc: "BIT",
  },
];

export default function Partners() {
  const { t, i18n } = useTranslation(["common", "meta"]);
  const { language } = i18n;
  const [showAll, setShowAll] = useState(false);
  const { setSendMessageOpen } = useAppProvider();
  const tlRef = useRef<gsap.core.Timeline | null>(null);

  const filteredPartners = useMemo(() => {
    if (language && language.includes("en")) {
      return partners.filter((p) => p.title !== "RecFaces");
    }
    return partners;
  }, [language]);

  const clickBecomePartner = () => {
    window.scrollTo(0, 0);
    setSendMessageOpen(true);
  };

  useLayoutEffect(() => {
    const tl = gsap.timeline();
    if (showAll) {
      tlRef.current = tl.to(`.${styles["cards-list"]}`, { height: "auto" });
    } else {
      tlRef.current !== null && tlRef.current.reverse();
      tlRef.current = null;
    }
  }, [showAll]);

  return (
    <PageWrapper>
      <CustomHead title={t("for_partners")!} desc={t("meta:partners_page")!} />
      <PageDescription
        title={t("for_partners")}
        description={t("partnersPage.description")}
      />
      <SloganLink
        text={
          <span>
            {t("partnersPage.cooperation_1")}
            <br />
            {t("partnersPage.cooperation_2")}
            <br />
            {t("partnersPage.cooperation_3")}
          </span>
        }
        btnTitle={t("partnersPage.become_partners")}
        handleClick={clickBecomePartner}
      />
      <MaxWidth addclasses={[styles["to-section"]]}>
        <Advantage
          icon={CaseIcon}
          title={t("partnersPage.vendors")}
          description={t("partnersPage.vendors_desc")}
          addclasses={[styles["advantage"]]}
        />
        <Advantage
          icon={ScreenIcon}
          title={t("partnersPage.integrators")}
          description={t("partnersPage.integrators_desc_p1")}
          addclasses={[styles["advantage"]]}
        />
      </MaxWidth>
      <MaxWidth>
        <GridContainer addclasses={[styles["cards-section"]]}>
          <h4>{t("partnersPage.already_working")}</h4>
          <div className={styles["cards-list"]}>
            {filteredPartners.map((partner) => (
              <PartnerCard
                title={t(`companies.name.${partner.title}`)}
                logo={partner.logo}
                logoSmall={partner.logoSmall!}
                desc={t(`companies.description.${partner.desc}`)!}
                key={partner.title}
              />
            ))}
          </div>
          <OutlinedBtn
            handleClick={() => setShowAll((prev) => !prev)}
            title={showAll ? t("hide") : t("partnersPage.show_more")}
            addclasses={[styles["btn"]]}
          />
        </GridContainer>
      </MaxWidth>
      <Map />
      <Discuss />
    </PageWrapper>
  );
}
